import { createContext, Dispatch, SetStateAction, useCallback, useContext, useState } from 'react';
import { Point, Area } from 'react-easy-crop';

import { getCroppedImg } from './helpers';

export type Image = { data: string; fileName: string };

interface ImageCropContextModel {
  image?: Image;
  setImage: Dispatch<SetStateAction<Image | undefined>>;
  zoom: number;
  setZoom: Dispatch<SetStateAction<number>>;
  crop: Point;
  setCrop: Dispatch<SetStateAction<Point>>;
  croppedAreaPixels?: Area;
  setCroppedAreaPixels: Dispatch<SetStateAction<Area | undefined>>;
  onCropComplete?: (croppedArea: Area, croppedAreaPixels: Area) => void;
  getProcessedImage: () => Promise<File | undefined>;
  resetStates: () => void;
}

const ImageCropContext = createContext<ImageCropContextModel>({} as ImageCropContextModel);

const defaultZoom = 1;
const defaultImage = undefined;
const defaultCrop = { x: 0, y: 0 };
const defaultCroppedAreaPixels = { width: 0, height: 0, x: 0, y: 0 };

export const ImageCropProvider = ({ children }) => {
  const [image, setImage] = useState(defaultImage);
  const [crop, setCrop] = useState(defaultCrop);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(defaultCroppedAreaPixels);
  const [zoom, setZoom] = useState(defaultZoom);

  const onCropComplete = useCallback((_: Area, _croppedAreaPixels: Area) => {
    setCroppedAreaPixels(_croppedAreaPixels);
  }, []);

  const getProcessedImage = async () => {
    if (image && croppedAreaPixels) {
      return await getCroppedImg(image, croppedAreaPixels);
    }
  };

  const resetStates = () => {
    setImage(defaultImage);
    setCrop(defaultCrop);
    setCroppedAreaPixels(defaultCroppedAreaPixels);
  };

  return (
    <ImageCropContext.Provider
      value={{
        image,
        setImage,
        zoom,
        setZoom,
        crop,
        setCrop,
        croppedAreaPixels,
        setCroppedAreaPixels,
        onCropComplete,
        getProcessedImage,
        resetStates,
      }}
    >
      {children}
    </ImageCropContext.Provider>
  );
};

export const useImageCropContext = () => {
  const context = useContext(ImageCropContext);

  if (!context) {
    throw new Error('useImageCropContext must be used in a component wrapped with ImageCropContextProvider');
  }
  return context;
};
